import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {Modal, Button} from 'react-bootstrap';
import RndModal from '../../../components/modals/RndModal';
import moment from 'moment';

const FolderInfoSection = styled.div`
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;

  h4 {
    margin: 0 0 10px 0;
    color: #495057;
    font-size: 16px;
    font-weight: 600;
  }

  .folder-details {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 8px 16px;
    font-size: 14px;
    align-items: baseline;
    justify-items: baseline;

    .label {
      font-weight: 600;
      color: #6c757d;
    }

    .value {
      color: #495057;
      word-break: break-all;
    }
  }
`;

const EmailListSection = styled.div`
  flex: 1;
  padding: 15px;
  overflow-y: auto;

  h4 {
    margin: 0 0 10px 0;
    color: #495057;
    font-size: 16px;
    font-weight: 600;
  }

  .email-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    h4 {
      margin: 0;
    }

    .search-input {
      padding: 6px 12px;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      font-size: 14px;
      width: 300px;

      &:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
      }

      &::placeholder {
        color: #6c757d;
      }
    }
  }

  .email-list {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 4px;
  }

  .email-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #f8f9fa;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f8f9fa;
    }

    &:last-child {
      border-bottom: none;
    }

    &.selected {
      background-color: #dceefb;
    }

    .email-info {
      flex: 1;

      .email-address {
        font-weight: 600;
        color: #495057;
        margin-bottom: 4px;
      }

      .email-date {
        font-size: 12px;
        color: #6c757d;
        margin-bottom: 4px;
      }

      .email-subject {
        font-size: 12px;
        color: #6c757d;
        margin-bottom: 6px;
      }

      .email-linked-warning {
        font-size: 13px;
        color: #e74c3c;
        font-weight: 500;
        margin-top: 6px;

        a {
          color: #e74c3c;
          text-decoration: underline;
          cursor: pointer;

          &:hover {
            color: #c0392b;
            text-decoration: underline;
          }
        }
      }

      .email-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;

        .tag {
          background-color: #e9ecef;
          border: 1px solid #adb5bd;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 11px;
          color: #495057;
          font-weight: 500;
        }
      }
    }
  }

  .no-emails {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
    font-style: italic;
  }
`;

const LinkedEmailSection = styled.div`
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;

  h4 {
    margin: 0 0 10px 0;
    color: #856404;
    font-size: 16px;
    font-weight: 600;
  }

  .linked-email-info {
    background-color: white;
    border: 1px solid #ffeaa7;
    border-radius: 6px;
    padding: 12px;

    .email-address {
      font-weight: 600;
      color: #495057;
      margin-bottom: 4px;
    }

    .email-date {
      font-size: 12px;
      color: #6c757d;
      margin-bottom: 4px;
    }

    .email-subject {
      font-size: 12px;
      color: #6c757d;
      margin-bottom: 6px;
    }

    .email-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;

      .tag {
        background-color: #e9ecef;
        border: 1px solid #adb5bd;
        border-radius: 12px;
        padding: 2px 8px;
        font-size: 11px;
        color: #495057;
        font-weight: 500;
      }
    }
  }

  .warning-text {
    margin-top: 10px;
    font-size: 13px;
    color: #856404;
    font-style: italic;
  }

  .unlink-section {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #ffeaa7;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .unlink-text {
    flex: 1;
    margin-right: 15px;
    font-size: 13px;
    color: #856404;
  }
`;

const ModalFooterStyled = styled(Modal.Footer)`
  .selected-count {
    display: flex;
    align-items: center;
    margin-right: auto;
    color: #6c757d;
    font-size: 14px;
  }
`;

class LinkEmailToFolderModal extends Component {
  state = {
    selectedEmail: null,
    searchQuery: ''
  };

  handleEmailToggle = (email) => {
    this.setState(prevState => {
      // If clicking the same email, deselect it; otherwise select the new one
      const selectedEmail = prevState.selectedEmail && prevState.selectedEmail.message_id === email.message_id
        ? null
        : email;

      return { selectedEmail };
    });
  };

  handleSearchChange = (event) => {
    this.setState({ searchQuery: event.target.value });
  };

  filterEmails = (emails, searchQuery) => {
    if (!searchQuery.trim()) {
      return emails;
    }

    const query = searchQuery.toLowerCase();
    return emails.filter(email => {
      const from = (email.msg_from || '').toLowerCase();
      const subject = (email.msg_subject || '').toLowerCase();
      return from.includes(query) || subject.includes(query);
    });
  };

  renderTags = (tagsString) => {
    if (!tagsString || !tagsString.trim()) {
      return null;
    }

    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);

    if (tags.length === 0) {
      return null;
    }

    return (
      <div className="email-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {tags.map((tag, index) => (
          <span 
            key={index} 
            className="tag"
            style={{
              backgroundColor: '#e9ecef',
              border: '1px solid #adb5bd',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '11px',
              color: '#495057',
              fontWeight: '500'
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    );
  };

  handleSubmit = () => {
    const { selectedEmail } = this.state;
    const { folder, onSubmit } = this.props;

    if (selectedEmail) {
      onSubmit(folder, selectedEmail);
    }
  };

  handleUnlink = () => {
    const { folder, linkedEmail, onUnlink } = this.props;

    // Don't unlink if folder has WOMemfis field
    if (folder && folder.WOMemfis) {
      return;
    }

    if (linkedEmail && onUnlink) {
      onUnlink(linkedEmail);
    }
  };

  handleCancel = () => {
    this.setState({ selectedEmail: null });
    this.props.onClose();
  };

  renderFolderInfo = () => {
    const { folder } = this.props;
    
    return (
      <FolderInfoSection>
        <h4>Folder Information</h4>
        <div className="folder-details">
          <span className="label">Name:</span>
          <span className="value">{(folder && (folder.name || folder.FileName)) || 'Unknown'}</span>

          <span className="label">Full Path:</span>
          <span className="value">{(folder && (folder.fullPath || folder.FullPath)) || 'Unknown'}</span>

          <span className="label">Size:</span>
          <span className="value">{(folder && (folder.formattedSize || folder.size)) || 'Unknown'}</span>

          <span className="label">Modified:</span>
          <span className="value">{(folder && (moment(folder.modifiedOn).format('MM/DD/YYYY hh:mm:ss A'))) || 'Unknown'}</span>
        </div>
      </FolderInfoSection>
    );
  };

  renderLinkedEmailSection = () => {
    const { linkedEmail, folder } = this.props;
    const hasWOMemfis = folder && folder.WOMemfis;

    return (
      <LinkedEmailSection>
        <h4>Currently Linked Email</h4>
        <div className="linked-email-info">
          <div className="email-address">{linkedEmail.msg_from}</div>
          <div className="email-date">{linkedEmail.msg_date}</div>
          <div className="email-subject">{linkedEmail.msg_subject}</div>
          {this.renderTags(linkedEmail.msg_tags)}
        </div>
        <div className="warning-text">
          This folder is already linked to the email shown above.
        </div>
        <div className="unlink-section">
          <div className="unlink-text">
            {hasWOMemfis
              ? "Unlinking is disabled because this folder is associated with a work order."
              : "Click this button to remove the link between this folder and email."
            }
          </div>
          <Button
            bsStyle="danger" 
            onClick={this.handleUnlink}
            disabled={hasWOMemfis}
          >
            Unlink Email
          </Button>
        </div>
      </LinkedEmailSection>
    );
  };

  renderEmailListSection = () => {
    const { emails } = this.props;
    const { selectedEmail, searchQuery } = this.state;
    const filteredEmails = this.filterEmails(emails || [], searchQuery);

    return (
      <EmailListSection>
        <div className="email-header">
          <h4>Select Email to Link</h4>
          <input
            type="text"
            className="search-input"
            placeholder="Search by sender or subject"
            value={searchQuery}
            onChange={this.handleSearchChange}
          />
        </div>
        <div className="email-list">
          {filteredEmails && filteredEmails.length > 0 ? (
            filteredEmails.map((email) => {
              const isSelected = selectedEmail && selectedEmail.message_id === email.message_id;
              return (
                <div
                  key={email.message_id}
                  className={`email-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => this.handleEmailToggle(email)}
                >
                  <div className="email-info">
                    <div className="email-address">{email.msg_from}</div>
                    <div className="email-date">{email.msg_date}</div>
                    <div className="email-subject">{email.msg_subject}</div>
                    {this.renderTags(email.msg_tags)}
                    {email.asset_url && (
                      <div className="email-linked-warning">
                        <a
                          href={email.asset_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Already linked to a different folder - Click to view
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : searchQuery.trim() ? (
            <div className="no-emails">No emails match your search</div>
          ) : (
            <div className="no-emails">No emails available to link</div>
          )}
        </div>
      </EmailListSection>
    );
  };

  render() {
    const { isVisible, linkedEmail } = this.props;
    const { selectedEmail } = this.state;

    if (!isVisible) {
      return null;
    }

    return (
      <RndModal
        show={true}
        onHide={this.handleCancel}
        backdrop="static"
        enforceFocus={false}
        bsSize="large"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {linkedEmail ? 'Folder Already Linked' : 'Link Email with Folder'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflow: 'unset', padding: '0' }}>
          <div className="container-fluid" style={{ padding: '0' }}>
            {this.renderFolderInfo()}
            {linkedEmail ? this.renderLinkedEmailSection() : this.renderEmailListSection()}
          </div>
        </Modal.Body>
        <ModalFooterStyled>
          <div className="selected-count">
            {linkedEmail ? 'Folder already linked' : (selectedEmail ? 'Email selected.' + (selectedEmail.asset_url ? ' Already linked to a different folder. Want to override?' : '') : 'No email selected.')}
          </div>
          <Button bsStyle="default" onClick={this.handleCancel}>
            {linkedEmail ? 'Close' : 'Cancel'}
          </Button>
          {!linkedEmail && (
            <Button
              bsStyle="primary"
              onClick={this.handleSubmit}
              disabled={!selectedEmail}
            >
              Link Selected Email
            </Button>
          )}
        </ModalFooterStyled>
      </RndModal>
    );
  }
}

LinkEmailToFolderModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  folder: PropTypes.object,
  emails: PropTypes.arrayOf(PropTypes.shape({
    message_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    msg_from: PropTypes.string,
    msg_subject: PropTypes.string,
    msg_date: PropTypes.string,
    msg_tags: PropTypes.string,
  })),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onUnlink: PropTypes.func,
  linkedEmail: PropTypes.object
};

LinkEmailToFolderModal.defaultProps = {
  emails: [],
  linkedEmail: null,
  onUnlink: null
};

export default LinkEmailToFolderModal;